/**
 * The fourteen tools, as data.
 *
 * Eight broker verbs on the Jig origin, two chart scoped tools, one published
 * pattern tool, and three yarn service tools on the partner origin. The names,
 * descriptions, schemas and annotations here are the design's tool catalogue
 * transcribed, so what the build validator checks and what an agent reads are the
 * same strings.
 *
 * Descriptions follow the platform guidance rather than reading as prose for a
 * person. Each says what the tool does and when to use it, in positive language.
 * None says what not to do, none prescribes a step sequence, and none asks the
 * model to convert a value before passing it, because raw input is accepted and
 * normalised on this side. Every parameter description names its unit.
 *
 * `writes` and `carriesExternalContent` are the declared behaviour, and
 * `defineTool` derives `readOnlyHint` and `untrustedContentHint` from them. See
 * `types.ts`.
 *
 * Covers requirements 10.1, 11.1, 11.2, 11.4, 11.5, 2.2, 21.2, 21.4, 21.5, 26.4.
 */

import { defineTool } from './types';
import type { AnyToolSpec, ToolSpec } from './types';

/**
 * The origins the catalogue needs in order to name them in `exposedTo`.
 *
 * Structurally the `Origins` record `@jig/origins` resolves, so an app passes
 * `ORIGINS` straight in, but this package does not import it. Keeping the
 * dependency out means the build validator and the CI script can read the
 * catalogue in a plain Node process, where `import.meta.env` does not exist.
 */
export interface CatalogueOrigins {
  /** The workbench origin, the one the yarn tools are exposed to. */
  readonly jig: string;
  /** The partner yarn origin, the consuming origin for the published pattern. */
  readonly yarns: string;
}

/**
 * The prefix every partner registered tool carries, per requirement 2.2, so a
 * visiting agent that can see partner tools tells a data source from a workbench
 * verb.
 */
export const PARTNER_PREFIX = 'yarns_';

/**
 * The sentence every yarn tool description carries, per requirement 2.3. Written
 * out in each description rather than concatenated, because a description is a
 * string the validator measures and an agent reads, not a template.
 */
export const PARTNER_DATA_SOURCE_CLAUSE =
  'This is a data source for a pattern tool';

// ---------- broker verbs, Jig origin, hook registration ----------
//
// All eight register through `useToolSpec`, which wraps `useWebMCP`. None needs
// `exposedTo` and none needs signal driven unregistration, so the hook is the
// right path: it feature detects, no ops without `document.modelContext`, and
// unregisters on unmount for free. Req 10.1.

/** Input of {@link readPattern}. Both parameters narrow an otherwise full read. */
export interface ReadPatternInput {
  readonly size?: string;
  readonly section?: string;
}

export const readPattern = defineTool<ReadPatternInput>({
  name: 'read_pattern',
  description:
    'Returns the current pattern numbers: gauge, finished measurements, stitch counts, row counts, shaping distributions and skein counts. Use it to see the state of the document before proposing a change, and to confirm a change landed. Call with no arguments for a summary across all sizes, or name one size to get that size in full. Yarn name and fibre may come from a yarn database outside this site.',
  inputSchema: {
    type: 'object',
    properties: {
      size: {
        type: 'string',
        description: 'Size label such as M or 2X. Omit for a summary of every size.',
      },
      section: {
        type: 'string',
        description: 'One of yoke, body, sleeve, neckband, hem. Omit for all sections.',
      },
    },
  },
  writes: false,
  carriesExternalContent: true,
  registration: 'hook',
});

/** Input of {@link readChart}. */
export interface ReadChartInput {
  readonly panel: string;
  readonly form?: 'text' | 'grid';
}

export const readChart = defineTool<ReadChartInput>({
  name: 'read_chart',
  description:
    'Returns the stitch chart for a panel as ordered text: for each row, the row number, the working direction, and each stitch group in worked order with its stitch count. Use it to read the cable panel, to check where repeat boundaries fall, and to describe the chart to someone who is not looking at the screen.',
  inputSchema: {
    type: 'object',
    required: ['panel'],
    properties: {
      /*
       * The example has to be an identifier a document actually carries.
       *
       * This said "for example cable-front" and no panel has ever had that id: the seeded
       * one is `cable`, labelled `cable panel`. A model does what the description tells it,
       * so the first `read_chart` call any model made was refused, every time, for a value
       * this schema had handed it. Same defect as the `castOnCount` one on `edit_number`,
       * and the same fix: name what exists. `read_chart.panel.example` in
       * `apps/jig/src/agent/broker.test.ts` asserts every panel-like token named here
       * resolves against the seeded document.
       */
      panel: {
        type: 'string',
        description: 'Panel id or label. The seeded pattern draws one: id cable, label cable panel.',
      },
      form: {
        type: 'string',
        enum: ['text', 'grid'],
        description: 'text for worked order rows, grid for symbol rows.',
      },
    },
  },
  writes: false,
  carriesExternalContent: false,
  registration: 'hook',
});

/** Input of {@link setGauge}. Both counts are over four inches of swatch. */
export interface SetGaugeInput {
  readonly stsPer4in: number;
  readonly rowsPer4in: number;
}

export const setGauge = defineTool<SetGaugeInput>({
  name: 'set_gauge',
  description:
    'Sets the gauge for the whole pattern from a knitted swatch, in stitches and rows over four inches. Every stitch count, row count and yarn amount is recomputed from the new gauge while finished measurements stay as they are. Use it when the designer has swatched, or after a yarn change. Pass the numbers as measured, including fractions.',
  inputSchema: {
    type: 'object',
    required: ['stsPer4in', 'rowsPer4in'],
    properties: {
      stsPer4in: {
        type: 'number',
        description: 'Stitches counted over four inches of swatch, for example 22.',
      },
      rowsPer4in: {
        type: 'number',
        description: 'Rows counted over four inches of swatch, for example 30.',
      },
    },
  },
  writes: true,
  carriesExternalContent: false,
  registration: 'hook',
});

/** Input of {@link gradeSizes}. Bust figures are body measurements, not finished. */
export interface GradeSizesInput {
  readonly fromSize: string;
  readonly minBustIn: number;
  readonly maxBustIn: number;
  readonly sizeCount?: number;
}

export const gradeSizes = defineTool<GradeSizesInput>({
  name: 'grade_sizes',
  description:
    'Produces the full size run from the sample size across a bust range, up to thirteen sizes, applying two to four inches of fitting ease over body bust. Sizes appear one at a time as each is computed, and any size already computed is kept if the run is stopped. Use it to resize the sample garment across a publishable size range.',
  inputSchema: {
    type: 'object',
    required: ['fromSize', 'minBustIn', 'maxBustIn'],
    properties: {
      fromSize: {
        type: 'string',
        description: 'Sample size label to grade from, for example M.',
      },
      minBustIn: {
        type: 'number',
        description: 'Smallest body bust in the run, in inches.',
      },
      maxBustIn: {
        type: 'number',
        description: 'Largest body bust in the run, in inches.',
      },
      sizeCount: {
        type: 'integer',
        description: 'How many sizes to produce, 2 to 13. Defaults to 9.',
      },
    },
  },
  writes: true,
  carriesExternalContent: false,
  registration: 'hook',
});

/** Input of {@link editNumber}. One number, one size. */
export interface EditNumberInput {
  readonly size: string;
  readonly field: string;
  readonly value: number;
}

export const editNumber = defineTool<EditNumberInput>({
  name: 'edit_number',
  description:
    'Sets one number on one size, such as a cast on count, a row count or a finished measurement. The value is checked against the panel repeat, the raglan increase arithmetic and the size run before it is accepted, and a value that does not fit comes back with the rule it breaks and the nearest values that work. Use it for a single targeted correction.',
  inputSchema: {
    type: 'object',
    required: ['size', 'field', 'value'],
    properties: {
      size: {
        type: 'string',
        description: 'Size label such as M or 2X.',
      },
      /*
       * Every field named, as a closed set, rather than one example and a hope.
       *
       * It used to be a bare string with "for example yokeCastOn or armholeDepthIn" in the
       * description, and the first thing a model reaches for when asked to change a cast on is
       * `castOnCount`, which does not exist. That was the very first request a reader made of
       * the deployed agent and it failed. A model shown an enum picks from the enum.
       *
       * The names are the agent's own spelling because a caller has to pass one back, and
       * `apps/jig/src/agent/input.ts` keeps the same list keyed on `NumericField`, so a field
       * added to a size fails to typecheck there until it has a unit and a place in the set.
       */
      field: {
        type: 'string',
        enum: [
          'yokeCastOn',
          'bodyStitches',
          'increaseRounds',
          'bodyBustIn',
          'finishedBustIn',
          'bodyLengthIn',
          'armholeDepthIn',
          'crossBackIn',
          'neckDropIn',
          'sleeveLengthIn',
          'sleeveCapDepthIn',
          'sleeveAreaIn2',
          'yardsNeeded',
          'skeins',
        ],
        description:
          'Which number to change. yokeCastOn is the cast on stitch count. Names ending In are inches.',
      },
      value: {
        type: 'number',
        description:
          'New value. Stitch and row counts are whole numbers, measurements are inches.',
      },
    },
  },
  writes: true,
  carriesExternalContent: false,
  registration: 'hook',
});

/** Input of {@link substituteYarn}. */
export interface SubstituteYarnInput {
  readonly yarnQuery: string;
}

export const substituteYarn = defineTool<SubstituteYarnInput>({
  name: 'substitute_yarn',
  description:
    'Swaps the pattern to a different yarn, looking it up in a yarn database at another site, then recomputes gauge derived counts and skein counts for every size and reports the change in whole skeins. Use it when the designer wants a different yarn, or wants to know whether a substitute needs more skeins. Pass the yarn as the designer said it, brand and name together.',
  inputSchema: {
    type: 'object',
    required: ['yarnQuery'],
    properties: {
      yarnQuery: {
        type: 'string',
        description:
          'Yarn as the designer named it, for example Brooklyn Tweed Shelter worsted.',
      },
    },
  },
  writes: true,
  carriesExternalContent: true,
  registration: 'hook',
});

/** Input of {@link auditPattern}. The whole document audit takes no arguments. */
export type AuditPatternInput = Record<never, never>;

export const auditPattern = defineTool<AuditPatternInput>({
  name: 'audit_pattern',
  description:
    'Checks the finished pattern for internal contradictions across every size: panel repeats that do not divide, yoke targets the increase rounds cannot reach, shaping that will not land on whole rows, measurements that shrink as sizes grow, and sleeve caps inconsistent with armhole depth. Each finding names the rule, the size, the value and the values that would work. Use it before export.',
  inputSchema: {
    type: 'object',
    properties: {},
  },
  writes: false,
  carriesExternalContent: false,
  registration: 'hook',
});

/** Input of {@link exportPattern}. */
export interface ExportPatternInput {
  readonly format?: 'markdown' | 'plain';
}

/**
 * The one broker verb whose registration is conditional: the hook's `enabled`
 * flag is driven by whether the most recent audit found violations, which is
 * requirements 15.3, 15.4 and 18.6 with no extra machinery. `readOnlyHint` is
 * true because it reads and formats and does not modify the document.
 */
export const exportPattern = defineTool<ExportPatternInput>({
  name: 'export_pattern',
  description:
    'Returns the finished pattern for all sizes as structured text: gauge, finished measurements, stitch counts, row counts, shaping instructions and skein counts. Available once an audit reports no contradictions. Use it when the pattern is ready to publish or hand to a test knitter. Yarn name and fibre may come from a yarn database outside this site.',
  inputSchema: {
    type: 'object',
    properties: {
      format: {
        type: 'string',
        enum: ['markdown', 'plain'],
        description: 'markdown for headed sections, plain for unformatted text.',
      },
    },
  },
  writes: false,
  carriesExternalContent: true,
  registration: 'hook',
});

/** The eight verbs of requirement 10.1, in the order that requirement lists them. */
export const BROKER_TOOLS: ReadonlyArray<AnyToolSpec> = Object.freeze([
  readPattern,
  readChart,
  setGauge,
  gradeSizes,
  editNumber,
  substituteYarn,
  auditPattern,
  exportPattern,
]);

// ---------- chart scoped tools, Jig origin, raw registration ----------
//
// Both register through `registerToolRaw` with an `AbortController` per selected
// panel, because requirement 15.2 specifies unregistration by aborting the signal
// supplied at registration and the hook does not expose that signal. Deselecting
// the panel aborts the controller.

/** Input of {@link checkPanelFit}. */
export interface CheckPanelFitInput {
  readonly stitches: number;
}

export const checkPanelFit = defineTool<CheckPanelFitInput>({
  name: 'check_panel_fit',
  description:
    'Reports whether a stitch count fits the selected panel, and when it does not, the nearest counts above and below that do. Use it to test a count before writing it, so a proposal lands first time.',
  inputSchema: {
    type: 'object',
    required: ['stitches'],
    properties: {
      stitches: {
        type: 'integer',
        description: 'Stitch count to test against the selected panel, for example 86.',
      },
    },
  },
  writes: false,
  carriesExternalContent: false,
  registration: 'raw',
});

/** Input of {@link setChartCell}. */
export interface SetChartCellInput {
  readonly row: number;
  readonly col: number;
  readonly symbol: string;
}

export const setChartCell = defineTool<SetChartCellInput>({
  name: 'set_chart_cell',
  description:
    "Sets one stitch symbol in the selected panel's chart at a row and column. The chart and its text reading both update from the same change. Use it to correct a single symbol.",
  inputSchema: {
    type: 'object',
    required: ['row', 'col', 'symbol'],
    properties: {
      row: {
        type: 'integer',
        description: 'Chart row, counted from the bottom starting at 1.',
      },
      col: {
        type: 'integer',
        description: 'Chart column, counted from the right starting at 1.',
      },
      symbol: {
        type: 'string',
        description: 'One of k, p, yo, k2tog, ssk, c4f, c4b, no-stitch.',
      },
    },
  },
  writes: true,
  carriesExternalContent: false,
  registration: 'raw',
});

/** The two panel scoped tools, registered and unregistered with the selection. */
export const CHART_TOOLS: ReadonlyArray<AnyToolSpec> = Object.freeze([
  checkPanelFit,
  setChartCell,
]);

// ---------- published pattern tool, Jig origin, raw with exposedTo ----------

/** Input of the published pattern tool. Every measurement is a body measurement. */
export interface FitPublishedPatternInput {
  readonly bustIn: number;
  readonly backLengthIn?: number;
  readonly sleeveLenIn?: number;
}

/**
 * Raw registration is mandatory here, since `exposedTo` names the consuming
 * origins and the hook does not expose that option, which is exactly what
 * requirement 26.4 states. `carriesExternalContent` is false because this tool
 * returns engine validated numbers and size labels, never a partner sourced
 * string.
 *
 * The consuming origin is the partner origin. Neither the design's catalogue nor
 * task 6.1 names which origins consume the published pattern, and the partner is
 * the only other origin this project deploys, so it is the only consumer that can
 * be demonstrated. Widening the list is a configuration change, not a code
 * change.
 */
export const fitPublishedPattern = (
  origins: CatalogueOrigins,
): ToolSpec<FitPublishedPatternInput> =>
  defineTool<FitPublishedPatternInput>({
    name: 'fit_published_pattern',
    description:
      "Returns the pattern values for the graded size closest to a set of body measurements, together with any measurement that falls outside the graded range. Every value returned has passed the same checks the designer's own document passes. Use it to work a published pattern at your own measurements.",
    inputSchema: {
      type: 'object',
      required: ['bustIn'],
      properties: {
        bustIn: {
          type: 'number',
          description: 'Body bust in inches.',
        },
        backLengthIn: {
          type: 'number',
          description: 'Nape to hem in inches. Optional.',
        },
        sleeveLenIn: {
          type: 'number',
          description: 'Underarm to wrist in inches. Optional.',
        },
      },
    },
    writes: false,
    carriesExternalContent: false,
    registration: 'raw',
    exposedTo: [origins.yarns],
  });

// ---------- yarn service tools, partner origin, raw with exposedTo ----------
//
// All three register with `exposedTo` naming the Jig origin and carry the
// `yarns_` prefix per requirement 2.2, state in their descriptions that they are
// a data source for the broker verbs per requirement 2.3, and set
// `readOnlyHint` true per requirement 21.5. All three carry external content: a
// yarn catalogue holds names, fibre descriptions and community notes the site
// does not author and does not vouch for.

/** Input of the catalogue search. */
export interface YarnsFindYarnInput {
  readonly query: string;
  readonly weight?: string;
}

export const yarnsFindYarn = (origins: CatalogueOrigins): ToolSpec<YarnsFindYarnInput> =>
  defineTool<YarnsFindYarnInput>({
    name: 'yarns_find_yarn',
    description:
      'Searches the yarn catalogue by brand, name, weight or fibre and returns matching yarns with their ids. This is a data source for a pattern tool: results are intended for use through the Jig broker verbs, which validate them before any pattern changes. Pass the query as the person said it.',
    inputSchema: {
      type: 'object',
      required: ['query'],
      properties: {
        query: {
          type: 'string',
          description: 'Free text search, for example worsted wool or Shelter.',
        },
        weight: {
          type: 'string',
          description:
            'Optional weight filter: fingering, sport, dk, worsted, aran.',
        },
      },
    },
    writes: false,
    carriesExternalContent: true,
    registration: 'raw',
    exposedTo: [origins.jig],
  });

/** Input of the single yarn read. */
export interface YarnsYarnProfileInput {
  readonly yarnId: string;
}

export const yarnsYarnProfile = (
  origins: CatalogueOrigins,
): ToolSpec<YarnsYarnProfileInput> =>
  defineTool<YarnsYarnProfileInput>({
    name: 'yarns_yarn_profile',
    description:
      "Returns one yarn's weight, recommended gauge range, yards per skein and fibre content. This is a data source for a pattern tool: values are intended for use through the Jig broker verbs, which validate them before any pattern changes.",
    inputSchema: {
      type: 'object',
      required: ['yarnId'],
      properties: {
        yarnId: {
          type: 'string',
          description: 'Yarn id from a catalogue search result.',
        },
      },
    },
    writes: false,
    carriesExternalContent: true,
    registration: 'raw',
    exposedTo: [origins.jig],
  });

/** Input of the substitution list. */
export interface YarnsSubstitutionCandidatesInput {
  readonly yarnId: string;
  readonly limit?: number;
}

export const yarnsSubstitutionCandidates = (
  origins: CatalogueOrigins,
): ToolSpec<YarnsSubstitutionCandidatesInput> =>
  defineTool<YarnsSubstitutionCandidatesInput>({
    name: 'yarns_substitution_candidates',
    description:
      'Returns yarns that substitute for a given yarn, with their gauge ranges, yards per skein and community contributed notes. Notes are written by the public and are passed through as text for a person to read. This is a data source for a pattern tool: values are intended for use through the Jig broker verbs, which validate them before any pattern changes.',
    inputSchema: {
      type: 'object',
      required: ['yarnId'],
      properties: {
        yarnId: {
          type: 'string',
          description: 'Yarn id to find substitutes for.',
        },
        limit: {
          type: 'integer',
          description: 'How many candidates to return, 1 to 10. Defaults to 5.',
        },
      },
    },
    writes: false,
    carriesExternalContent: true,
    registration: 'raw',
    exposedTo: [origins.jig],
  });

/** The three tools the partner origin registers. Req 21.2. */
export const yarnServiceTools = (
  origins: CatalogueOrigins,
): ReadonlyArray<AnyToolSpec> =>
  Object.freeze([
    yarnsFindYarn(origins),
    yarnsYarnProfile(origins),
    yarnsSubstitutionCandidates(origins),
  ]);

// ---------- the whole catalogue ----------

/**
 * All fourteen specs for a given pair of origins.
 *
 * A function rather than a constant because four of the fourteen name an origin
 * in `exposedTo`, and an origin is deployment configuration: the local dev pair
 * and the deployed pair have to produce the same descriptors with different
 * origins in them. Everything the validator checks (task 3.5) and everything
 * Property 10 asserts (task 3.14) is independent of which pair it is given.
 */
export const buildCatalogue = (
  origins: CatalogueOrigins,
): ReadonlyArray<AnyToolSpec> =>
  Object.freeze([
    ...BROKER_TOOLS,
    ...CHART_TOOLS,
    fitPublishedPattern(origins),
    ...yarnServiceTools(origins),
  ]);

/** How many tools the catalogue holds. Named so a missing spec fails loudly. */
export const CATALOGUE_SIZE = 14;
